/**
 * Shared keyboard clearance for screens, dialogs, and bottom sheets on iOS and Android.
 *
 * Android needs this inset despite adjustResize in the manifest: the root
 * KeyboardProvider (App.tsx) runs the activity edge-to-edge, so the window does
 * not shrink, and it sets each Modal window to SOFT_INPUT_ADJUST_NOTHING after
 * React Native's ADJUST_RESIZE. Both platforms report the keypad height (iOS
 * includes the Done bar). Callers pad by that height instead of adding a
 * second offset.
 */
import React, { useEffect, useState, type RefObject } from 'react';
import {
  Keyboard,
  Platform,
  Pressable,
  ScrollView,
  StatusBar,
  StyleSheet,
  View,
  useWindowDimensions,
  type DimensionValue,
  type KeyboardEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { spacing } from '../../theme/spacing';

export const KEYBOARD_OVERLAY_PADDING = 20;

/**
 * Top inset for a keyboard-open dialog. iOS already reports the Dynamic Island
 * through safe area. Android's shorter status bar needs one small spacing token
 * below it so the dialog does not appear pinned to the system chrome.
 */
export function keyboardDialogTopInset(
  safeTop: number,
  statusBarHeight: number | null | undefined,
  platform: typeof Platform.OS = Platform.OS,
): number {
  const systemInset = Math.max(safeTop, statusBarHeight ?? 0);
  return platform === 'android' ? systemInset + spacing.sm : systemInset;
}

type KeyboardEventNames = {
  show: 'keyboardWillShow' | 'keyboardDidShow';
  hide: 'keyboardWillHide' | 'keyboardDidHide';
  change: 'keyboardWillChangeFrame' | 'keyboardDidChangeFrame';
};

function keyboardEventNames(): KeyboardEventNames | null {
  if (Platform.OS === 'ios') {
    return {
      show: 'keyboardWillShow',
      hide: 'keyboardWillHide',
      change: 'keyboardWillChangeFrame',
    };
  }
  if (Platform.OS === 'android') {
    return {
      show: 'keyboardDidShow',
      hide: 'keyboardDidHide',
      change: 'keyboardDidChangeFrame',
    };
  }
  return null;
}

function readKeyboardHeight(event: KeyboardEvent): number {
  const height = event.endCoordinates?.height ?? 0;
  return height > 0 ? height : 0;
}

/** Keypad height in pixels. 0 when hidden, and always 0 on web. */
export function useKeyboardHeight(): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const names = keyboardEventNames();
    if (!names) {
      return undefined;
    }
    const apply = (event: KeyboardEvent) => {
      const next = readKeyboardHeight(event);
      setHeight(prev => (prev === next ? prev : next));
    };
    const show = Keyboard.addListener(names.show, apply);
    const change = Keyboard.addListener(names.change, apply);
    const hide = Keyboard.addListener(names.hide, () => setHeight(0));
    return () => {
      show.remove();
      change.remove();
      hide.remove();
    };
  }, []);

  return height;
}

/**
 * Scroll a block to `topGap` below the top of the scroll content while the
 * keypad is open. Used when the focused field is the last control, so results
 * opening under it do not push the field away.
 */
export function usePinBlockAboveKeyboard({
  active,
  keyboardHeight,
  scrollRef,
  contentRef,
  blockRef,
  contentPadding,
  topGap,
}: {
  active: boolean;
  keyboardHeight: number;
  scrollRef: RefObject<ScrollView | null>;
  contentRef: RefObject<View | null>;
  blockRef: RefObject<View | null>;
  contentPadding: number;
  topGap: number;
}): void {
  useEffect(() => {
    if (!active || keyboardHeight === 0) {
      return undefined;
    }
    let cancelled = false;
    let layoutFrame = 0;
    // The keyboard height also changes scroll padding in the same commit.
    // Measure on the frame after that padding has laid out.
    const frame = requestAnimationFrame(() => {
      layoutFrame = requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        const block = blockRef.current;
        const content = contentRef.current;
        if (!block || !content) {
          return;
        }
        block.measureLayout(
          content,
          (_x, y) => {
            if (cancelled) {
              return;
            }
            scrollRef.current?.scrollTo({
              y: Math.max(y + contentPadding - topGap, 0),
              animated: true,
            });
          },
          () => undefined,
        );
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(layoutFrame);
    };
  }, [
    active,
    blockRef,
    contentPadding,
    contentRef,
    keyboardHeight,
    scrollRef,
    topGap,
  ]);
}

/**
 * Full-screen dim layer that keeps its child above the keypad.
 * The scroll box ends at the keypad, and the card stays centered in the
 * space under the status bar. `disabled` keeps the plain overlay.
 *
 * Long selectable lists belong in `KeyboardInsetSheet`, which uses this same
 * keypad height. Do not add a second inset on top of either component.
 */
export const KeyboardInsetScroll: React.FC<{
  children: React.ReactNode;
  disabled?: boolean;
}> = ({ children, disabled = false }) => {
  const keyboardHeight = useKeyboardHeight();
  const { top: safeTop } = useSafeAreaInsets();
  const avoid = !disabled && Platform.OS !== 'web';
  const topInset =
    keyboardHeight > 0
      ? keyboardDialogTopInset(safeTop, StatusBar.currentHeight)
      : 0;

  if (!avoid) {
    return <View style={styles.overlay}>{children}</View>;
  }

  return (
    <View style={styles.backdrop}>
      <ScrollView
        style={[styles.scroll, { marginBottom: keyboardHeight }]}
        contentContainerStyle={[
          styles.overlayInScroll,
          {
            paddingTop: KEYBOARD_OVERLAY_PADDING + topInset,
            paddingBottom: KEYBOARD_OVERLAY_PADDING,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        bounces={false}
        scrollEnabled={keyboardHeight > 0}
        testID="confirmation-keyboard-scroll"
      >
        {children}
      </ScrollView>
    </View>
  );
};

const DEFAULT_SHEET_LIST_MAX_HEIGHT = 360;

/**
 * Tallest the card gets while the keypad is open. The gap above the keys is
 * most of the screen; filling it hides the page. This cap leaves that page
 * visible and still fits the title, search field, and several rows.
 */
export const KEYBOARD_OPEN_SHEET_MAX_HEIGHT = 400;

/**
 * Bottom sheet that stays above the keypad on iOS and Android.
 *
 * Native modals do not shrink when the keypad opens. The card sits above the
 * keypad at a fixed compact height, and the list fills that card. A shorter
 * gap (small phone, tall keypad) wins over the cap so the card cannot run
 * under the keys or the status bar. Web stays centered and does not inset.
 */
export const KeyboardInsetSheet: React.FC<{
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  cardStyle?: StyleProp<ViewStyle>;
  /** Cap on the card while the keypad is closed. */
  idleMaxHeight?: DimensionValue;
  /** Cap on the scrolling body while the keypad is closed. */
  listMaxHeight?: number;
  testID?: string;
  listTestID?: string;
  onBackdropPress?: () => void;
}> = ({
  children,
  header,
  footer,
  cardStyle,
  idleMaxHeight = '80%',
  listMaxHeight = DEFAULT_SHEET_LIST_MAX_HEIGHT,
  testID = 'keyboard-inset-sheet',
  listTestID = 'keyboard-inset-sheet-list',
  onBackdropPress,
}) => {
  const keyboardHeight = useKeyboardHeight();
  const { height: windowHeight } = useWindowDimensions();
  const { top: safeTop, bottom: safeBottom } = useSafeAreaInsets();
  const lift = Platform.OS === 'web' ? 0 : keyboardHeight;
  const topInset =
    lift > 0 ? keyboardDialogTopInset(safeTop, StatusBar.currentHeight) : 0;
  const sheetMaxHeight = Math.max(0, windowHeight - lift - topInset);
  const openCardHeight =
    lift > 0 ? Math.min(sheetMaxHeight, KEYBOARD_OPEN_SHEET_MAX_HEIGHT) : 0;

  return (
    <View
      style={[
        styles.sheetHost,
        Platform.OS === 'web' && styles.sheetHostWeb,
        lift === 0 && safeBottom > 0 && { paddingBottom: safeBottom },
      ]}
      testID={testID}
    >
      {onBackdropPress ? (
        <Pressable
          style={styles.sheetBackdrop}
          onPress={onBackdropPress}
          accessibilityRole="button"
          accessibilityLabel="Dismiss list"
          testID={`${testID}-backdrop`}
        />
      ) : null}
      <View
        testID={`${testID}-card`}
        style={[
          styles.sheetCard,
          { maxHeight: idleMaxHeight },
          cardStyle,
          lift > 0 && {
            marginBottom: lift,
            height: openCardHeight,
            maxHeight: openCardHeight,
          },
        ]}
      >
        {header}
        <ScrollView
          testID={listTestID}
          style={[
            styles.sheetList,
            lift > 0 ? styles.sheetListOpen : { maxHeight: listMaxHeight },
          ]}
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
          showsVerticalScrollIndicator
        >
          {children}
        </ScrollView>
        {footer}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: KEYBOARD_OVERLAY_PADDING,
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  scroll: {
    flex: 1,
  },
  overlayInScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: KEYBOARD_OVERLAY_PADDING,
  },
  sheetHost: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'flex-end',
  },
  sheetHostWeb: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  sheetBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  sheetCard: {
    width: '100%',
    overflow: 'hidden',
    zIndex: 1,
    flexGrow: 0,
  },
  sheetList: {
    flexGrow: 0,
  },
  sheetListOpen: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
  },
});
